import React, { useState, useEffect } from 'react';
import { useAtomValue } from "jotai";
import { LicenseRequestViewAtom } from "../../atoms";
import { LicenseRequestType } from "../../utils/enums/LicenseRequestType";
import { ViewLicense } from "./ViewLicense";
import { StripeEmbeddedForm } from "../stripe/StripeEmbeddedForm";
import { LicenseRequestDeclined } from "./LicenseRequestDeclined";
import { LicenseRequestFailed } from "./LicenseRequestFailed";
import MintingLoadingScreen from "../lottie-animations/MintingLoadingScreen";
import Box from "@mui/material/Box";
import { LicenseRequestPurchased } from "./LicenseRequestPurchased";
import { StripeProductTypes } from "../../utils/enums/StripeProductTypes";
import axios from 'axios';
import { toast } from 'react-toastify';
import { doc, updateDoc, getDoc } from "firebase/firestore";
import { db, auth } from "../../config/firebase";
import { Typography } from "@mui/material";
import { MintBanner } from '../banner/MintBanner';

export const LicenseSend = () => {
  const licenseRequest = useAtomValue(LicenseRequestViewAtom);
  const [isLoading, setIsLoading] = useState(false);
  const [transferError, setTransferError] = useState(null);
  const [publicAddress, setPublicAddress] = useState("");
  const [allChecksPassed, setAllChecksPassed] = useState(false);

  console.log(licenseRequest)

  useEffect(() => {
    const fetchWalletAddress = async () => {
      try {
        if (licenseRequest?.recipientPublicAddress) {
          setPublicAddress(licenseRequest.recipientPublicAddress);
          setAllChecksPassed(true);

          // Update user data if we have a valid license request with recipientPublicAddress
          if (auth.currentUser) {
            const userRef = doc(db, 'users', auth.currentUser.uid);
            const userSnap = await getDoc(userRef);

            if (userSnap.exists()) {
              const userData = userSnap.data();
              if (!userData.walletAddress) {
                // Update user's walletAddress with the recipientPublicAddress
                await updateDoc(userRef, {
                  walletAddress: licenseRequest.recipientPublicAddress
                });
              }
            }
          }
          return;
        }

        if (!auth.currentUser) return;

        const userRef = doc(db, 'users', auth.currentUser.uid);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const userData = userSnap.data();
          if (userData.walletAddress) {
            setPublicAddress(userData.walletAddress);
            setAllChecksPassed(true);
          }
        }
      } catch (error) {
        console.error("Error fetching/updating wallet address:", error);
      }
    };

    fetchWalletAddress();
  }, [licenseRequest]);

  // Force allChecksPassed to true if we have a valid publicAddress
  useEffect(() => {
    if (publicAddress && publicAddress.length > 0 && !allChecksPassed) {
      setAllChecksPassed(true);
    }
  }, [publicAddress, allChecksPassed]);

  const handleTransferTrigger = async () => {
    console.log("Attempting to trigger license transfer for request:", licenseRequest?.uid);
    setTransferError(null);
    if (!licenseRequest || !licenseRequest?.uid){
      return;
    }

    const licenseTokenId = licenseRequest?.licenseUid;
    const licenseRequestUid = licenseRequest?.uid;
    const recipientEmail = licenseRequest?.recipientMail;
    const senderEmail = licenseRequest?.email;

    if (!publicAddress || !licenseTokenId || !recipientEmail || !senderEmail) {
      const errorMsg = "Missing data required for license transfer.";
      console.error(errorMsg, { publicAddress, licenseTokenId, recipientEmail, senderEmail });
      toast.error(`Cannot transfer license: ${errorMsg}`);
      setTransferError(errorMsg);
      return;
    }

    setIsLoading(true);

    const transferUrl = process.env.REACT_APP_ENVIRONMENT === 'development' ?
        'http://localhost:5001/c3-nft-c3/us-central1/transferLicense' :
        'https://us-central1-c3-nft-c3.cloudfunctions.net/transferLicense';

    const params = {
      publicAddress: publicAddress,
      licenseRequestUid: licenseRequestUid,
      recipientEmail: recipientEmail,
      senderEmail: senderEmail
    };

    try {
      console.log("Calling /transferLicense with params:", params);
      const response = await axios.get(transferUrl, { params });
      console.log("Transfer License response:", response.data);

      if (response.data?.status === 'success') {
        toast.success("License transferred successfully!");
        await updateDoc(doc(db, "license_requests", licenseRequest.uid), {
          state: LicenseRequestType.MINTED,
          transferTxnHash: response.data.transactionHash
        });
        // navigate(`/portfolio?nftUid=${licenseRequest?.nftUid}`)
      } else {
        const errorMsg = response.data?.message || "License transfer failed.";
        console.error("Transfer failed:", errorMsg);
        toast.error(`Transfer failed: ${errorMsg}`);
        setTransferError(errorMsg);
        await updateDoc(doc(db, "license_requests", licenseRequest.uid), { state: LicenseRequestType.FAILED });
      }
    } catch (error) {
      console.error('Error calling transferLicense endpoint:', error);
      const errorMsg = error.response?.data?.message || error.response?.data || error.message || "An unexpected error occurred during transfer.";
      toast.error(`Transfer failed: ${errorMsg}`);
      setTransferError(errorMsg);
      await updateDoc(doc(db, "license_requests", licenseRequest.uid), { state: LicenseRequestType.FAILED });
    } finally {
      setIsLoading(false);
    }
  };

  console.log(licenseRequest?.state);

  const renderContent = () => {
    if (!licenseRequest || !licenseRequest?.state) {
      return <Typography>Loading license request...</Typography>;
    }

    switch (licenseRequest.state) {
      case LicenseRequestType.PENDING:
        return <ViewLicense publicAddress={publicAddress} setPublicAddress={setPublicAddress} />;

      case LicenseRequestType.ACCEPTED:
        if (isLoading) {
          return <Box sx={{position: "relative", minHeight: "50dvh"}}><MintingLoadingScreen message={"Transferring your license..."} /></Box>;
        }
        if (transferError) {
          return <LicenseRequestFailed errorMessage={transferError} />;
        }
        console.log(licenseRequest?.licenseUid);
        // Safety check - force allChecksPassed to true if we have a valid address
        const hasValidAddress = publicAddress && publicAddress.length > 0;
        console.log("publicAddress && publicAddress.length > 0");
        console.log(publicAddress);
        console.log(publicAddress?.length > 0);
        return (
          <>
            <MintBanner
              setAccount={setPublicAddress}
            />
            {publicAddress && publicAddress.length > 0 && (
              <StripeEmbeddedForm
                nftUid={licenseRequest?.nftUid}
                publicAddress={publicAddress}
                productType={StripeProductTypes.LICENSE}
                allChecksPassed={hasValidAddress} // Use direct check instead of state
                setStatus={(status) => console.log("Stripe Status:", status)}
                onNextStep={handleTransferTrigger}
                licenseUid={licenseRequest?.licenseUid}
                licenseRequestUid={licenseRequest?.uid}
              />
            )}
          </>
        );

      case LicenseRequestType.DECLINED:
        return <LicenseRequestDeclined />;

      case LicenseRequestType.PAID:
        return <Box sx={{ position: "relative", minHeight: "50dvh" }}><MintingLoadingScreen message={"Processing license transfer..."} /></Box>;

      case LicenseRequestType.MINTED:
        return <LicenseRequestPurchased />;

      case LicenseRequestType.FAILED:
        return <LicenseRequestFailed errorMessage={transferError || "The license request failed."} />;

      default:
        return <Typography>Unknown license state: {licenseRequest.state}</Typography>;
    }
  };

  return <>{renderContent()}</>;
}